import {setGlobalTheme} from "@atlaskit/tokens";
import {createContext, useContext, useSyncExternalStore} from "react";
import {anankePreferencesZodValidator, defaultAnankePreferences, type AnankePreferences} from "@vesta/ananke/domainModels/anankeConfiguration";
import {jsonZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

// TODO: Figure out the configuration/preferences nomenclature thingy

type Listener = () => void;

function anankeClientSetTheme(theme: "light" | "dark") {
    // // TODO: Figure out how I can conditionally print this
    // // if (context.intellsysDevtoolsClient.getConfiguration().insaneLogs.theming === true) {
    // console.groupCollapsed(+new Date(), "theming", "__root", "setGlobalTheme");
    // console.info("theme", theme);
    // console.groupEnd();
    // // }

    setGlobalTheme({
        colorMode: theme,
        typography: "typography",
        //
    });
    // This function is defined in index.html, so we can ignore ts and eslint warnings here
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    setTheme(theme);
}

export class AnankeClient {
    private configuration: AnankePreferences;
    private listeners: Set<Listener> = new Set();

    constructor() {
        // TODO: Create a good localStorage wrapper for things like this (for type safety)
        const anankePreferencesResult = jsonZodValidator.pipe(anankePreferencesZodValidator).safeParse(localStorage.getItem("ananke"));
        let anankePreferences: AnankePreferences;
        if (anankePreferencesResult.success === true) {
            anankePreferences = anankePreferencesResult.data;
        } else {
            anankePreferences = defaultAnankePreferences;
        }

        const configuration = anankePreferences;

        this.configuration = configuration;

        anankeClientSetTheme(this.configuration.theme);
    }

    // Get current configuration
    getConfiguration(): AnankePreferences {
        return this.configuration;
    }

    // Update configuration and notify subscribers
    setConfiguration(updater: AnankePreferences | ((prev: AnankePreferences) => AnankePreferences)): void {
        const oldTheme = this.configuration.theme;
        if (typeof updater === "function") {
            this.configuration = updater(this.configuration);
        } else {
            this.configuration = updater;
        }
        const newTheme = this.configuration.theme;

        // TODO: Create a good localStorage wrapper for things like this (for type safety)
        localStorage.setItem("ananke", JSON.stringify(this.configuration));

        if (newTheme !== oldTheme) {
            anankeClientSetTheme(newTheme);
        }

        // Notify all listeners
        this.notify();
    }

    // Subscribe to changes
    subscribe(listener: Listener): () => void {
        this.listeners.add(listener);

        // Return unsubscribe function
        return () => {
            this.listeners.delete(listener);
        };
    }

    // Notify all subscribers
    private notify(): void {
        for (const listener of this.listeners) {
            listener();
        }
    }
}

type AnankeClientContextData = AnankeClient;

export const AnankeClientContext = createContext<AnankeClientContextData | undefined>(undefined);

// Hook to get the client instance
export function useAnankeClient(): AnankeClient {
    const client = useContext(AnankeClientContext);
    if (client === undefined) {
        throw new Error("AnankeClientContext.Provider not found");
    }
    return client;
}

// Hook to subscribe to configuration changes
export function useAnankePreferences(): AnankePreferences {
    const client = useAnankeClient();

    // useSyncExternalStore is the modern way to subscribe to external state
    const anankePreferences = useSyncExternalStore(
        // Subscribe function
        (callback) => client.subscribe(callback),
        // Get snapshot function
        () => client.getConfiguration(),
        // Get server snapshot (for SSR, same as client for this case)
        () => client.getConfiguration(),
    );

    return anankePreferences;
}
